import * as React from "react";
import {
  formSet,
  formSetValue,
  useCpFormAnyValue,
  useCpFormAnyValueId,
  useCpFormValue,
} from "../../gvc/testNewForm/utils";
import { BasePathContext, useBasePath } from "../../../reactContexts/basePathContext";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ContentLayout } from "../../../components/layout/contentLayout";
import { SingleNavMenuLink, toLinkButton, toLinkPage, toLinkSpace } from "../../identity/useNavMenu";
import { useAppDispatch } from "../../../store/hooks";
import { setRedirect } from "../../../store/contextSlice";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../../envVariables";
import { SingleNavMenu } from "../../../components/antd/layout/singleNavMenu";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { CpTagsNew } from "../../../components/form/CpTagsNew";
import { homeLink, request } from "../../../services/cpln";
import { CloudAccountCloudProviderOptions } from "../utils";
import { Provider } from "./provider/provider";
import { General } from "./general";
import { CpRootError } from "../../gvc/testNewForm/CpRootError";
import { Drawers } from "./drawers";

interface Props {
  ignoreBlockerRef: React.RefObject<boolean>;
}
export const CloudAccountCreateContent: React.FC<Props> = ({ ignoreBlockerRef }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/cloudaccount/-create/*`);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValue(`meta.submitting`);

  React.useEffect(() => {
    fetchInstructions();

    const startingPage = `${basePath}/general`;
    navigate(startingPage, { replace: true });
  }, []);

  const provider = useCpFormValue("provider");
  const azureUrl = useCpFormValue("azure.url");

  const providerLabel = React.useMemo(() => {
    console.log("provider changed, calculating providerLabel");
    return CloudAccountCloudProviderOptions.find((o) => o.value === provider)?.label;
  }, [provider]);

  React.useEffect(() => {
    if (azureUrl === undefined) {
      return;
    }
    if (!azureUrl.includes("?code=")) {
      return;
    }
    const [newUrl, newCode] = azureUrl.split("?code=");
    if (newUrl && newCode) {
      formSetValue(formId, "azure.url", newUrl);
      formSetValue(formId, "azure.code", newCode);
    }
  }, [azureUrl]);

  async function fetchInstructions() {
    try {
      const { data } = await request({ url: `${homeLink("cloudaccount")}/-instructions/aws` });
      formSetValue(formId, "aws.instructions", data);
    } catch (e) {}
    try {
      const { data } = await request({ url: `${homeLink("cloudaccount")}/-instructions/gcp` });
      formSetValue(formId, "gcp.instructions", data);
    } catch (e) {}
  }

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("Cloud Account", "/general", { names: [`name`, `description`] }),
      toLinkPage(`Provider${providerLabel ? ` | ${providerLabel}` : ""}`, "/provider", {
        names: [`aws`, `azure`, `gcp`, `ngs`],
      }),
      toLinkPage("Tags", "/tags", { names: ["tags"] }),
      toLinkSpace(),
      toLinkButton("Cancel", {
        type: "primary",
        onClick: () => {
          ignoreBlockerRef.current = true;
          const back = pathname.split(`/-create`)[0];
          dispatch(setRedirect(back));
        },
        disabled: submitting,
        danger: true,
      }),
      toLinkButton("Preview", {
        type: "default",
        onClick: () => formSet(formId, `meta.previewing`, true),
        disabled: submitting,
      }),
      toLinkButton("Create", {
        type: "primary",
        onClick: async () => {
          await submit();
        },
        loading: submitting,
        disabled: submitting,
        className: "action",
      }),
    );

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
        }),
      );
    }

    return links;
  }, [submitting, providerLabel]);

  return (
    <BasePathContext.Provider value={basePath}>
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={false} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route path={`general/*`} element={<General />} />
              <Route path={`provider/*`} element={<Provider />} />
              <Route path={`tags/*`} element={<CpTagsNew />} />
            </Routes>
            <Drawers />
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
